<script setup lang="ts">
import {
  FormInline,
  FormLabel,
  FormInput,
  FormSelect,
  FormCheck
} from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import Button from "../../../base-components/Button";
import { Ref, computed, onMounted, ref, watch } from "vue";
import { reactive, toRefs } from "vue";
import { required, email, helpers, maxLength } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { getAuth } from "firebase/auth";
import { userUserStore } from "../../../stores/user";
import {
  doc,
  getDoc,
  getFirestore,
  limit,
  orderBy,
  setDoc
} from "@firebase/firestore";
import Alert from "../../../base-components/Alert";
import Lucide from "../../../base-components/Lucide";
import { validarRut } from "../../../utils/validarRut";

import {
  getDownloadURL,
  getStorage,
  ref as storageRef,
  uploadBytes
} from "firebase/storage";
import { asignarCustomClaim } from "../../../services/cloudFunctions/asignarCustomClaim";
import { collection, getDocs, query, where } from "firebase/firestore";
import { verificarCodigoPromocional } from "../../../services/cloudFunctions/verificarCodigoPromocional";
//import { consultarRutDGI } from "../../../services/cloudFunctions/consultarRutDGI";
import { getCurrentTime } from "../../../utils/fecha";
//import { localidades } from "../../../stores/localidades";
import { registrarEmpresa } from "../../../services/cloudFunctions/registrarEmpresa";
import {
  usuarioConverter,
  UsuarioEmpresas,
  CodigoPromocional
} from "@fixeduy/restaurantestypes";

const ambiente = import.meta.env.VITE_ENV_NOMBRE;
const db = getFirestore();
const userStore = userUserStore();

const emit = defineEmits<{ (e: "registroListo"): void }>();

const loading = ref<boolean>(false);
const mensajeErrorServidor = ref<string>("");
const esLaBanca = ref(false);

const formData = reactive({
  usarMailPrinccipal: false,
  mailPrincipal: "",
  rut: "",
  razonSocial: "",
  nombreComercial: "",
  domicilio: "",
  localidad: "",
  departamento: "",
  celular: "",
  cedula: "",
  documentoExtranjero: "",
  primerNombre: "",
  segundoNombre: "",
  primerApellido: "",
  segundoApellido: "",
  tieneContador: true,
  contadorNombre: "",
  contadorMail: "",
  contadorTelefono: "",
  literalE: "no" as "no" | "pendienteConfirmacion",
  plan: "ilimitado" as "50" | "ilimitado" | "stock",
  codigoPromocionalIngresado: "",
  pagoAnual: false,
  terminosAceptados: false,
  esCambioProveedor: false,
  rutMenos12Caracteres: false,
  solicitarFirma: true,
  solicitarFirmaAnos: 2
});

const validarEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const rules: any = computed(() => {
  const registroRules: any = {
    usarMailPrinccipal: {},
    mailPrincipal: {
      mail: {
        $validator: (mail: string) => mail == "" || validarEmail(mail),
        $message: "El mail no es correcto"
      }
    },
    rut: {
      required: helpers.withMessage("El RUT no puede ser vacío", required),
      rut: {
        $validator: (rut: string) => validarRut(rut),
        $message: "El RUT no es correcto"
      },
      largo: {
        $validator: (rut: string) => rut.length == 12,
        $message: "El RUT debe tener 12 números"
      }
    },
    razonSocial: {
      required: helpers.withMessage(
        "La razón social no puede ser vacía",
        required
      )
    },
    nombreComercial: {
      required: helpers.withMessage(
        "El nombre comercial no puede ser vacío",
        required
      )
    },
    domicilio: {
      required: helpers.withMessage(
        "El domicilio no puede ser vacío",
        required
      ),
      maxLength: helpers.withMessage(
        "El campo domicilio excedió el número de caracteres",
        maxLength(70)
      )
    },
    localidad: {
      required: helpers.withMessage("La localidad no puede ser vacía", required)
    },
    departamento: {
      required: helpers.withMessage(
        "El departamento no puede ser vacía",
        required
      )
    },
    celular: {
      required: helpers.withMessage(
        "Necesitamos nos brindes un teléfono para contactarte.",
        required
      )
    },
    tieneCedula: {},
    primerNombre: {},
    segundoNombre: {},
    primerApellido: {},
    segundoApellido: {},
    tieneContador: {},
    contadorNombre: {},
    contadorMail: {},
    contadorTelefono: {},
    plan: {
      required: helpers.withMessage("Selecciona que plan contratar", required)
    },
    pagoAnual: {},
    codigoPromocionalIngresado: {},
    terminosAceptados: {
      validacion: {
        $validator: (terms: boolean) => terms,
        $message: "Debes aceptar los términos y condiciones"
      }
    }
  };

  if (formData.solicitarFirma) {
    registroRules.primerNombre = {
      required: helpers.withMessage(
        "El primer nombre no puede ser vacío",
        required
      )
    };
    registroRules.primerApellido = {
      required: helpers.withMessage(
        "El primer apellido no puede ser vacío",
        required
      )
    };
  }

  return registroRules;
});

const validate = useVuelidate(rules, toRefs(formData));

let logo: any = null;
let constanciaLiteralE: any = null;
const imageData = ref<any>(null);
const storage = getStorage();

const generateUniqueFileName = () => {
  const timestamp = new Date().getTime(); // Obtiene el tiempo actual en milisegundos
  const randomPart = Math.random().toString(36).substring(2, 15); // Genera una cadena aleatoria
  return `${timestamp}-${randomPart}`;
};

const guardarFoto = async (archivo: any) => {
  if (archivo == null) {
    return null;
  }
  const extension = archivo.name.split(".").pop();

  const imgRef = storageRef(
    storage,
    `gestion/${generateUniqueFileName()}.${extension}`
  );

  const snapshot = await uploadBytes(imgRef, archivo);
  return snapshot.ref.fullPath;
};

const onFilePicked = (event: Event, archivo: string) => {
  const input = event.target as HTMLInputElement;
  let file;
  if (input && input.files) file = input.files[0];
  if (!file) throw new Error();
  if (archivo === "documento") logo = file;
  else if (archivo === "constanciaLiteralE") constanciaLiteralE = file;
  let reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = function () {
    imageData.value = reader.result;
  };

  reader.onerror = function () {
    console.log(reader.error);
  };
};

const registroHandler = async () => {
  loading.value = true;
  const result = await validate.value.$validate();
  if (result) {
    let fotoDocumentoURL = "";
    let constanciaLiteralEURL = "";
    try {
      const fotoDocumentoExtranjero = await guardarFoto(logo);
      const pdfConstanciaLiteralE = await guardarFoto(constanciaLiteralE);

      if (fotoDocumentoExtranjero != null) {
        fotoDocumentoURL = await getDownloadURL(
          storageRef(storage, fotoDocumentoExtranjero)
        );
      }

      if (pdfConstanciaLiteralE != null) {
        constanciaLiteralEURL = await getDownloadURL(
          storageRef(storage, pdfConstanciaLiteralE)
        );
      }
    } catch (error) {
      console.log(error);
    }
    registrarEmpresa({
      mailPrincipal: formData.mailPrincipal,
      rut: formData.rut,
      razonSocial: formData.razonSocial,
      nombreComercial: formData.nombreComercial,
      domicilio: formData.domicilio,
      localidad: formData.localidad,
      departamento: formData.departamento,
      celular: formData.celular,
      cedula: formData.cedula,
      documentoExtranjero: formData.documentoExtranjero,
      fotoDocumentoExtranjero: fotoDocumentoURL,
      constanciaLiteralE: constanciaLiteralEURL,
      primerNombre: formData.primerNombre,
      segundoNombre: formData.segundoNombre,
      primerApellido: formData.primerApellido,
      segundoApellido: formData.segundoApellido,
      tieneContador: formData.tieneContador,
      contadorNombre: formData.contadorNombre,
      contadorMail: formData.contadorMail,
      contadorTelefono: formData.contadorTelefono,
      plan: esLaBanca.value ? "50" : formData.plan,
      codigoPromocionalIngresado: formData.codigoPromocionalIngresado,
      pagoAnual: formData.pagoAnual,
      literalE: formData.literalE,
      esCambioProveedor: formData.esCambioProveedor,
      solicitarFirma: formData.solicitarFirma,
      solicitarFirmaAnos: formData.solicitarFirmaAnos,
      registroCPATU: false,
      registroAFU: false,
      registroLaBanca: esLaBanca.value
    })
      .then((result: { exito: any; errorMsg: string }) => {
        if (result.exito) {
          // devolvio true
          const docRef = doc(
            db,
            `usuarios/${userStore.userLoggedIn!.uid}`
          ).withConverter(usuarioConverter);

          getDoc(docRef).then(doc => {
            if (doc.exists()) {
              const usuario = doc.data();
              userStore.setEmpresasRegistradas(usuario.empresas);
              const empresaActual = usuario.empresas.filter(
                (x: UsuarioEmpresas) => x.rut == formData.rut
              );

              asignarCustomClaim({
                empresaSolicitada: empresaActual[0].id
              }).then(result => {
                const auth = getAuth();
                if (auth.currentUser != null) {
                  auth.currentUser
                    .getIdTokenResult(true)
                    .then(async idTokenResult => {
                      userStore.setUserToken(idTokenResult);
                      await userStore.setEmpresasActual(empresaActual[0]);
                      emit("registroListo");
                    });
                } else {
                  userStore.signOut();
                }
              });
            }
          });
        } else {
          loading.value = false;
          mensajeErrorServidor.value = result.errorMsg;
        }
      })
      .catch((error: any) => {
        console.log(error);
        loading.value = false;
        mensajeErrorServidor.value = `Ha ocurrido un error inesperado, por favor intenta nuevamente. Error: ${error}`;
      });
  } else {
    loading.value = false;
  }
};

const revisarRUT = async () => {
  if (formData.rut.length == 12) {
    formData.rutMenos12Caracteres = false;
  } else {
    formData.rutMenos12Caracteres = true;
  }
};

const codigoPromocional: Ref<CodigoPromocional | null> = ref(null);
const codigoPromocionalMostrarCargando = ref(false);
const codigoPromocionalMostrarMensaje = ref("");

const validarCodigoPromocional = async () => {
  codigoPromocional.value = null;
  codigoPromocionalMostrarMensaje.value = "";

  const codigoValidar = formData.codigoPromocionalIngresado
    .replaceAll(" ", "")
    .toUpperCase();
  if (codigoValidar == "") {
    return;
  }

  codigoPromocionalMostrarCargando.value = true;

  const resultado = await verificarCodigoPromocional({ codigo: codigoValidar });

  if (resultado.codigoPromocional == null) {
    codigoPromocionalMostrarMensaje.value =
      "El código promocional no es válido";
  } else if (resultado.codigoPromocional.activoHasta < new Date().getTime()) {
    codigoPromocionalMostrarMensaje.value =
      "El código promocional se encuentra vencido. No es válido.";
  } else {
    codigoPromocional.value = resultado.codigoPromocional;
  }
  codigoPromocionalMostrarCargando.value = false;
};

watch(
  () => formData.usarMailPrinccipal,
  () => {
    if (formData.usarMailPrinccipal) {
      formData.contadorMail = userStore.userLoggedIn!.email ?? "";
      formData.contadorNombre = userStore.userLoggedIn!.displayName ?? "";
    }
  }
);
function filtrarSoloNumeros(e: any) {
  e.target.value = e.target.value.replace(/\D/g, "");
  formData.celular = e.target.value;
}
</script>

<template>
  <form class="validate-form p-4" @submit.prevent="">
    <!-- RUT -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> RUT </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.rut.$model"
        :class="{
          'border-danger': validate.rut.$error
        }"
        placeholder="RUT"
        @blur="revisarRUT"
      />
    </FormInline>
    <template v-if="validate.rut.$error">
      <div
        v-for="(error, index) in validate.rut.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>

    <!-- <Alert
      variant="warning"
      class="mb-2 my-4"
      v-if="formData.esCambioProveedor"
    >
      Detectamos que vienes de otro proveedor. Luego de realizar el registro,
      favor de escribirnos a soporte@fixed.uy para coordinar la migración de tus
      datos.
    </Alert> -->

    <Alert
      variant="warning"
      class="mb-2 my-4"
      v-if="formData.rutMenos12Caracteres"
    >
      El RUT debe tener 12 números. Si tienes un RUT de 11 números, debes
      completar con un 0 a la izquierda.
    </Alert>

    <!-- Razón social -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Razón social </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.razonSocial.$model"
        :class="{
          'border-danger': validate.razonSocial.$error
        }"
        placeholder="Razón social"
      />
    </FormInline>
    <template v-if="validate.razonSocial.$error">
      <div
        v-for="(error, index) in validate.razonSocial.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Nombre comercial -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Nombre comercial </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.nombreComercial.$model"
        :class="{
          'border-danger': validate.nombreComercial.$error
        }"
        placeholder="Nombre comercial"
      />
    </FormInline>
    <template v-if="validate.nombreComercial.$error">
      <div
        v-for="(error, index) in validate.nombreComercial.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Domicilio -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Domicilio </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.domicilio.$model"
        :class="{ 'border-danger': validate.domicilio?.$error }"
        placeholder="Domicilio"
      />
    </FormInline>
    <template v-if="validate.domicilio.$error">
      <div
        v-for="(error, index) in validate.domicilio.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Localidad -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Ciudad / Localidad </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.localidad.$model"
        :class="{
          'border-danger': validate.localidad.$error
        }"
        placeholder="Ciudad / Localidad"
      />
    </FormInline>
    <template v-if="validate.localidad.$error">
      <div
        v-for="(error, index) in validate.localidad.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Departamento -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Departamento </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.departamento.$model"
        :class="{
          'border-danger': validate.departamento.$error
        }"
        placeholder="Departamento"
      />
    </FormInline>
    <template v-if="validate.departamento.$error">
      <div
        v-for="(error, index) in validate.departamento.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Celular -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Celular </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.celular.$model"
        :class="{
          'border-danger': validate.celular.$error
        }"
        placeholder="Celular"
        inputmode="numeric"
        @input="filtrarSoloNumeros"
      />
    </FormInline>
    <template v-if="validate.celular.$error">
      <div
        v-for="(error, index) in validate.celular.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <hr class="mt-5 pb-3" />

    <div class="mt-5">
      <FormInline>
        <FormLabel class="sm:w-40"> Código promocional </FormLabel>
        <FormInput
          type="text"
          v-model.trim="validate.codigoPromocionalIngresado.$model"
          :class="{
            'border-danger': validate.codigoPromocionalIngresado.$error
          }"
          @blur="validarCodigoPromocional"
          placeholder="Código promocional"
        />
      </FormInline>

      <template v-if="codigoPromocionalMostrarCargando">
        <div class="mt-5 text-center">
          <div
            class="flex flex-col items-center col-span-6 sm:col-span-3 xl:col-span-2"
          >
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </template>
      <template v-else>
        <template v-if="codigoPromocional != null">
          <template
            v-if="
              codigoPromocional.mesesGratisRegistro == 1 &&
              codigoPromocional.mesesGratisEmpresa == 1
            "
          >
            <p class="mt-3">
              Código promocional de {{ codigoPromocional.empresaNombre }}, que
              brindará a ambos un mes gratis de Atiendo.
            </p>
          </template>
          <template v-else-if="codigoPromocional.empresaNombre != ''">
            <p class="mt-3">
              Código promocional de {{ codigoPromocional.empresaNombre }}, que
              te otorga {{ codigoPromocional.mesesGratisRegistro }} meses
              gratis.
            </p>
          </template>
          <template v-else>
            <p class="mt-3">
              Código promocional que te otorga
              {{ codigoPromocional.mesesGratisRegistro }} meses gratis.
            </p>
          </template>
        </template>
        <Alert
          variant="warning"
          class="mb-2 my-4"
          v-if="
            codigoPromocional == null && codigoPromocionalMostrarMensaje != ''
          "
        >
          {{ codigoPromocionalMostrarMensaje }}
        </Alert>
      </template>
    </div>
    <hr class="mt-5 pb-3" />

    <!-- Primer nombre -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Primer nombre </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.primerNombre.$model"
        :class="{
          'border-danger': validate.primerNombre.$error
        }"
        placeholder="Primer nombre"
      />
    </FormInline>
    <template v-if="validate.primerNombre.$error">
      <div
        v-for="(error, index) in validate.primerNombre.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Segundo nombre -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Segundo nombre </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.segundoNombre.$model"
        :class="{
          'border-danger': validate.segundoNombre.$error
        }"
        placeholder="Segundo nombre"
      />
    </FormInline>
    <template v-if="validate.segundoNombre.$error">
      <div
        v-for="(error, index) in validate.segundoNombre.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Primer Apellido -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Primer apellido </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.primerApellido.$model"
        :class="{
          'border-danger': validate.primerApellido.$error
        }"
        placeholder="Primer apellido"
      />
    </FormInline>
    <template v-if="validate.primerApellido.$error">
      <div
        v-for="(error, index) in validate.primerApellido.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-- Segundo Apellido -->
    <FormInline class="mt-3">
      <FormLabel class="sm:w-40"> Segundo apellido </FormLabel>
      <FormInput
        type="text"
        v-model.trim="validate.segundoApellido.$model"
        :class="{
          'border-danger': validate.segundoApellido.$error
        }"
        placeholder="Segundo apellido"
      />
    </FormInline>
    <template v-if="validate.segundoApellido.$error">
      <div
        v-for="(error, index) in validate.segundoApellido.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>

    <div
      class="flex items-center mt-4 text-xs text-slate-950 dark:text-slate-300 sm:text-sm"
    >
      <FormCheck.Input
        type="checkbox"
        class="mr-2 border border-slate-700 dark:border-slate-600"
        v-model="formData.terminosAceptados"
      />
      <label class="cursor-pointer select-none" htmlFor="remember-me">
        Acepto los
        <a
          class="ml-1 text-primary dark:text-slate-300"
          href="https://atiendo.cloud/terminos-y-condiciones"
          target="_blank"
          >Términos y Condiciones</a
        >
        y la
        <a
          class="ml-1 text-primary dark:text-slate-300"
          href="https://atiendo.cloud/politica-de-privacidad"
          target="_blank"
          >Política de Privacidad</a
        >.
      </label>
    </div>
    <template v-if="validate.terminosAceptados.$error">
      <div
        v-for="(error, index) in validate.terminosAceptados.$errors"
        :key="index"
        class="mt-1 mb-5 text-danger"
      >
        {{ error.$message }}
      </div>
    </template>
    <!-------------------- Validación final ------------------------>
    <Alert
      variant="outline-danger"
      class="flex items-center my-4"
      v-if="validate?.$error"
    >
      <Lucide icon="AlertCircle" class="w-6 h-6 mr-2" />
      <ul>
        <li v-for="e in validate.$errors">{{ e.$message }}</li>
      </ul>
    </Alert>
    <template v-if="ambiente != 'Produccion'">
      <h2
        class="text-2xl my-10 font-bold text-center intro-x xl:text-3xl xl:text-left mb-3 text-danger"
      >
        Ambiente {{ ambiente }}, no será un registro real.
      </h2>
    </template>
    <div v-if="mensajeErrorServidor != ''" class="mt-5">
      <Alert variant="danger" class="flex items-center mb-2">
        <Lucide icon="AlertOctagon" class="w-6 h-6 mr-2" />
        {{ mensajeErrorServidor }}
      </Alert>
    </div>
    <div class="mt-5 text-center xl:mt-8 xl:text-left">
      <div v-if="loading">
        <div
          class="flex flex-col items-left justify-end col-span-6 sm:col-span-3 xl:col-span-2"
        >
          <LoadingIcon icon="oval" class="w-8 h-8" />
        </div>
      </div>
      <!------------------ Botón Registrarme ---------------------->
      <div v-else>
        <Button
          variant="primary"
          class="w-full px-4 py-3 align-top xl:w-32 xl:mr-3"
          @click="registroHandler"
        >
          Registrarme
        </Button>
      </div>
    </div>
  </form>
</template>
